import React, { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Tooltip,
    Popover,
    Paper,
} from "@mui/material";
import {
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import Header from "@/components/Header";
import { useLocation, Link } from "wouter";
import { Building, Calendar, ChevronLeft, ChevronRight, Home, LayoutDashboard, Menu, MessageSquare, NotepadText, Plus, Settings2, User } from "lucide-react";
import { useSelector } from "react-redux";

const menuItems: any = [
    {
        name: "Dashboard",
        icon: <LayoutDashboard size={20} />,
        path: "/dashboard",
    },
    {
        name: "Property",
        icon: <Building size={20} />,
        path: "/property",
    },
    {
        name: "Residents",
        icon: <User size={20} />,
        path: "/resident",
    },
    {
        name: "Vendors",
        icon: <User size={20} />,
        path: "/vendors",
    },
    {
        name: "Task",
        icon: <NotepadText size={20} />,
        path: "/task",
        children: [
            { name: "List", icon: <Menu size={18} />, path: "/task" },
            { name: "Create", icon: <Plus size={18} />, path: "/task/add" },
            { name: "Calendar", icon: <Calendar size={18} />, path: "/task/calendar" },
        ],
    },
    {
        name: "Meeting",
        icon: <Home size={20} />,
        path: "/meeting",
    },
    {
        name: "Message",
        icon: <MessageSquare size={20} />,
        path: "/message",
    },
    {
        name: "Feedback",
        icon: <MessageSquare size={20} />,
        path: "/feedback",
    },
    {
        name: "Settings",
        icon: <Settings2 size={20} />,
        path: "/settings",
        // children: [
        //     { name: "Profile", icon: <User size={18} />, path: "/settings/profile" },
        // ],
    },
];

const drawerWidth = 260;

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {

    const [location, navigate] = useLocation();
    const { token } = useSelector((state: any) => state?.auth);

    if (!token) {
        navigate(`/login`);
        return null;
    }

    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
    const [collapsed, setCollapsed] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [hoverMenu, setHoverMenu] = useState<string | null>(null);

    const handleToggleMenu = (menuName: string) => {
        setOpenMenus((prev) => ({ ...prev, [menuName]: !prev[menuName] }));
        setSelectedMenu(menuName);
    };

    const isActive = (item: any, path?: string) => {
        // if direct match
        if (path && location === path) return true;

        // if parent has children, check if any child's path matches
        if (item?.children && item?.children.some((sub: any) => location === sub.path)) {
            return true;
        }

        return false;
    }

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    width: collapsed ? 70 : drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: collapsed ? 70 : drawerWidth,
                        boxSizing: "border-box",
                        transition: "width 0.3s",
                    },
                }}
            >
                <div className="flex items-center justify-between p-2">
                    {!collapsed ? (
                        <h2 className="text-lg font-bold">
                            <Link
                                href="/dashboard"
                                className="flex items-center"
                                data-testid="link-logo"
                            >
                                <div className="text-2xl font-bold text-primary">
                                    <i className="fas fa-home mr-2"></i>Jire Properties
                                </div>
                            </Link>
                        </h2>
                    ) : <>
                        <h2 className="text-lg font-bold">
                            <Link
                                href="/dashboard"
                                className="flex items-center"
                                data-testid="link-logo"
                            >
                                <div className="text-2xl font-bold text-primary">
                                    <i className="fas fa-home mr-2"></i>
                                </div>
                            </Link>
                        </h2>
                    </>}
                    <button
                        className="text-black"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? <ChevronRight /> : <ChevronLeft />}
                    </button>
                </div>

                <List sx={{ mt: 1 }}>
                    {menuItems.map((item: any) => (
                        <React.Fragment key={item.name}>
                            <Tooltip title={collapsed ? item.name : ""} placement="right">
                                <ListItemButton
                                    onClick={(e) =>
                                        item.children ? handleToggleMenu(item.name) : navigate(item.path!)
                                    }
                                    sx={{
                                        backgroundColor: isActive(item, item.path) ? "#f2f8f4" : "transparent",
                                        "&:hover": { backgroundColor: "#f2f8f4" },
                                        borderLeft: isActive(item, item.path) ? "3px solid #15a537" : "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: isActive(item, item.path) ? "#15a537" : "black",
                                            minWidth: 40,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    {!collapsed && (
                                        <ListItemText
                                            sx={{
                                                color: isActive(item, item.path) ? "#15a537" : "",
                                                fontWeight: 600,
                                            }}
                                            primary={item.name}
                                        />
                                    )}
                                    {!collapsed &&
                                        item.children &&
                                        (openMenus[item.name] ? <ExpandLess /> : <ExpandMore />)}
                                </ListItemButton>

                            </Tooltip>

                            {/* Expanded submenu (only if not collapsed) */}
                            {!collapsed && item.children && (
                                <Collapse
                                    in={openMenus[item.name]}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        {item.children.map((sub: any) => (
                                            <ListItemButton
                                                key={sub.name}
                                                sx={{
                                                    pl: 4,
                                                    backgroundColor: location === sub.path ? "#f2f8f4" : "",
                                                    "&:hover": { backgroundColor: "#f2f8f4" },
                                                    borderLeft: location === sub.path ? "3px solid #15a537" : "none",
                                                    borderRadius: 2,
                                                }}
                                                onClick={() => navigate(sub.path!)}
                                            >
                                                {sub.icon && (
                                                    <ListItemIcon
                                                        sx={{
                                                            color: location === sub.path ? "#15a537" : "black",
                                                            minWidth: 35,
                                                        }}
                                                    >
                                                        {sub.icon}
                                                    </ListItemIcon>
                                                )}
                                                <ListItemText
                                                    sx={{ color: location === sub.path ? "#15a537" : "" }}
                                                    primary={sub.name}
                                                />
                                            </ListItemButton>

                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Drawer>

            {/* Hover popout submenu (only when collapsed) */}
            {collapsed && hoverMenu && (
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    disableRestoreFocus
                >
                    <List sx={{ minWidth: 180 }}>
                        {menuItems
                            .find((m: any) => m.name === hoverMenu)
                            ?.children?.map((sub: any) => (
                                <ListItemButton
                                    key={sub.name}
                                    sx={{
                                        "&:hover": { backgroundColor: "#f2f8f4" },
                                    }}
                                    onClick={() => navigate(sub.path!)}
                                >
                                    {sub.icon && (
                                        <ListItemIcon sx={{ color: isActive(sub.path, sub.name) ? "#15a537" : "black", minWidth: 35 }}>
                                            {sub.icon}
                                        </ListItemIcon>
                                    )}
                                    <ListItemText sx={{ color: isActive(sub.path, sub.name) ? "#15a537" : "" }} primary={sub.name} />
                                </ListItemButton>
                            ))}
                    </List>
                </Popover>
            )}

            {/* Main content */}
            <div className="flex-1">
                <Header />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <Paper 
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            borderRadius : 4,
                            boxShadow: "0px 1px 3px rgba(0,0,0,0.12)", 
                        }} 
                        elevation={0}
                    >
                        {children}
                    </Paper>
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
